"use client";

import Link from "next/link";
import { useEffect, useId, useMemo, useState } from "react";
import { Chess, type Square } from "chess.js";
import { BoardView } from "@/components/board/BoardView";
import { MoveList } from "@/components/board/MoveList";
import type { FamousGame } from "@/content/types";
import "./game-replay.css";
import { ExplanationFeed } from "./ExplanationFeed";

/** Every board position is reconstructed from the PGN, never from the artwork. */
export function GameReplay({ game }: { game: FamousGame }) {
  const moves = useMemo(() => {
    const c = new Chess();
    c.loadPgn(game.pgn);
    const r = new Chess();
    return c.history({ verbose: true }).map((m) => {
      r.move(m.san);
      return { san: m.san, from: m.from as Square, to: m.to as Square, fen: r.fen() };
    });
  }, [game.pgn]);
  const [ply, setPly] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [tab, setTab] = useState<"explanation" | "moves">("explanation");
  const id = useId();
  const noted = Object.keys(game.notes).map(Number).sort((a, b) => a - b);
  const nextMoment = noted.find((n) => n > ply);
  const [speed, setSpeed] = useState(1);
  const current = moves[ply - 1];
  const moveLabel = ply ? `${Math.ceil(ply / 2)}${ply % 2 ? "." : "..."} ${current.san}` : "Before the first move";
  const seek = (n: number) => { setPlaying(false); setPly(n); };

  useEffect(() => {
    if (!playing || ply >= moves.length) return;
    // Linger on annotated positions without interrupting continuous playback.
    const timer = setTimeout(() => {
      const next = ply + 1;
      setPly(next);
      if (next === moves.length) setPlaying(false);
    }, (game.notes[ply] ? 9000 : 3000) / speed);
    return () => clearTimeout(timer);
  }, [playing, ply, moves.length, game.notes, speed]);

  return (
    <article className="game-cinema">
      <div className="game-cinema-layout">
        <div className="game-stage">
          <header className="game-title">
            <Link href="/games" className="game-eyebrow">← Famous games</Link>
            <h1>{game.title}</h1>
            <p>{game.white} <span className="game-versus">vs</span> {game.black}</p>
            <p className="game-meta">{game.event} <span>·</span> {game.year} <span>·</span> <strong>{game.result}</strong></p>
          </header>
          <div className="game-board-wrap">
            <BoardView view="3d" cinematic cameraPreset="club" fen={current?.fen ?? new Chess().fen()} orientation={game.result === "0-1" ? "black" : "white"} movable="none" lastMove={current ? { from: current.from, to: current.to } : null} label={`${game.title}: ${moveLabel}`} />
          </div>
          <div className="game-playback" aria-label="Replay controls">
            <div className="game-transport">
              <button onClick={() => seek(0)} disabled={ply === 0} aria-label="First position">«</button>
              <button onClick={() => seek(ply - 1)} disabled={ply === 0} aria-label="Previous move">‹</button>
              <button className="game-play" aria-label={playing ? "Pause replay" : "Play replay"} onClick={() => { if (!playing && ply === moves.length) setPly(0); setPlaying(!playing); }}><span aria-hidden>{playing ? "Ⅱ" : "▶"}</span></button>
              <button onClick={() => seek(ply + 1)} disabled={ply === moves.length} aria-label="Next move">›</button>
              <button onClick={() => seek(moves.length)} disabled={ply === moves.length} aria-label="Final position">»</button>
            </div>
            <label className="game-progress"><span>{ply} / {moves.length} <small>half-moves</small></span><input aria-label="Game position" type="range" min={0} max={moves.length} value={ply} onChange={(e) => seek(Number(e.target.value))} /></label>
            <button className="game-next" disabled={nextMoment === undefined} onClick={() => { if (nextMoment !== undefined) seek(nextMoment); }}>Next key moment <span aria-hidden>→</span></button>
          </div>
          <div className="game-pace"><label>Playback speed <select value={speed} onChange={(event) => setSpeed(Number(event.target.value))}><option value={0.5}>0.5× · Slower</option><option value={1}>1× · Relaxed</option><option value={1.5}>1.5× · Faster</option></select></label><p className="game-playback-hint">Continuous playback · extra time at key moments</p></div>
        </div>

        <aside className="game-study" aria-label="Game study">
          <div className="game-tabs" role="tablist" aria-label="Study view">
            {(["explanation", "moves"] as const).map((name) => <button key={name} role="tab" id={`${id}-${name}-tab`} aria-controls={`${id}-${name}-panel`} aria-selected={tab === name} tabIndex={tab === name ? 0 : -1} onClick={() => setTab(name)} onKeyDown={(event) => {
              if (!["ArrowLeft", "ArrowRight", "Home", "End"].includes(event.key)) return;
              event.preventDefault();
              const target = event.key === "Home" ? "explanation" : event.key === "End" ? "moves" : name === "moves" ? "explanation" : "moves";
              setTab(target);
              document.getElementById(`${id}-${target}-tab`)?.focus();
            }}>{name === "explanation" ? "Explanation" : "Moves"}</button>)}
          </div>
          <div role="tabpanel" id={`${id}-explanation-panel`} aria-labelledby={`${id}-explanation-tab`} hidden={tab !== "explanation"} tabIndex={0} className="game-explanation">
            <ExplanationFeed game={game} moves={moves} ply={ply} visible={tab === "explanation"} />
          </div>
          <div role="tabpanel" id={`${id}-moves-panel`} aria-labelledby={`${id}-moves-tab`} hidden={tab !== "moves"} tabIndex={0} className="game-moves-panel">
            <div className="game-moves-intro"><h2>The complete score</h2><p>Select any move to inspect its position.</p></div>
            <MoveList moves={moves} current={ply} onSelect={seek} />
            <button className="game-return" onClick={() => setTab("explanation")}>Read explanation <span aria-hidden>→</span></button>
          </div>
        </aside>
      </div>
    </article>
  );
}

